import {
  createElement,
  CSSProperties,
  lazy,
  ReactNode,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Box,
  type CodeElementProps,
  notify,
  PreviewMarkdownCodeElement,
  Skeleton,
  tokens,
} from '@cast/design-system';
import type { Components, ExtraProps } from 'react-markdown';
import type { SxProps } from '@mui/material';

const Markdown = lazy(() => import('react-markdown'));

const markdownPrimaryColor = tokens.theme.fg.structural.primary;
const markdownLinkColor = tokens.theme.fg.action.primary;

type MarkdownTag =
  | 'p'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'li'
  | 'strong'
  | 'em'
  | 'blockquote'
  | 'del';

const markdownText = (Tag: MarkdownTag) => {
  const MarkdownText = ({
    node: _node,
    style,
    ...props
  }: ExtraProps & {
    style?: CSSProperties;
    children?: ReactNode;
    className?: string;
  }) =>
    createElement(Tag, {
      ...props,
      style: { color: markdownPrimaryColor, ...style },
    } as never);
  MarkdownText.displayName = `MarkdownText(${Tag})`;
  return MarkdownText;
};

const markdownTable: NonNullable<Components['table']> = ({
  node: _node,
  ...props
}) => (
  <Box
    sx={{
      overflow: 'auto',
      border: `1px solid ${tokens.theme.border.structural.neutral}`,
      borderRadius: '10px',
      background: tokens.theme.bg.structural.surface,
      my: 16,
    }}
  >
    <Box
      sx={{
        width: '100%',
        border: `1px solid ${tokens.theme.border.structural.neutral}`,
        borderRadius: '10px',
        borderSpacing: 2,
        borderCollapse: 'collapse',
        overflow: 'hidden',
      }}
      component="table"
      {...props}
    />
  </Box>
);

const markdownTd: NonNullable<Components['td']> = ({
  node: _node,
  ...props
}) => (
  <Box
    sx={{
      border: `1px solid ${tokens.theme.border.structural.neutral}`,
      p: '8px',
      textWrap: 'nowrap',
      background: tokens.theme.bg.structural.surface,
      color: markdownPrimaryColor,
    }}
    component="td"
    {...props}
  />
);

const markdownTh: NonNullable<Components['th']> = ({
  node: _node,
  ...props
}) => (
  <Box
    sx={{
      p: '8px',
      textWrap: 'nowrap',
      background: tokens.theme.bg.structural.surfaceRaised,
      color: markdownPrimaryColor,
    }}
    component="th"
    {...props}
  />
);

const markdownImg: NonNullable<Components['img']> = ({
  node: _node,
  ...props
}) => (
  <img
    {...props}
    alt={props.alt ?? ''}
    style={{
      maxWidth: '100%',
      maxHeight: '400px',
      width: 'auto',
      height: 'auto',
    }}
  />
);

const markdownTextComponents = {
  p: markdownText('p'),
  h1: markdownText('h1'),
  h2: markdownText('h2'),
  h3: markdownText('h3'),
  h4: markdownText('h4'),
  h5: markdownText('h5'),
  h6: markdownText('h6'),
  li: markdownText('li'),
  strong: markdownText('strong'),
  em: markdownText('em'),
  blockquote: markdownText('blockquote'),
  del: markdownText('del'),
  table: markdownTable,
  td: markdownTd,
  th: markdownTh,
  img: markdownImg,
} as Partial<Components>;

const defaultLinkComponent: NonNullable<Components['a']> = ({
  href,
  children,
}) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    style={{ color: markdownLinkColor }}
  >
    {children}
  </a>
);

const defaultCodeComponent = (props: CodeElementProps) => (
  <PreviewMarkdownCodeElement
    {...props}
    onCopy={() => notify.success('Copied to clipboard')}
  />
);

export type ChatMarkdownProps = {
  text: string;
  /** Override or extend the default markdown component renderers. */
  components?: Components;
  sx?: SxProps;
};

/**
 * Renders AI response text as markdown using the same component mapping
 * as the OpsPilot chat MessageBubble. Lazy-loads react-markdown and
 * remark-gfm to keep the host bundle small.
 */
export const ChatMarkdown = ({ text, components, sx }: ChatMarkdownProps) => {
  const [isRemarkGfmLoaded, setIsRemarkGfmLoaded] = useState(false);
  const remarkGfmRef = useRef<unknown>(null);

  useEffect(() => {
    const loadRemarkGfm = async () => {
      if (!remarkGfmRef.current) {
        const remarkGfmModule = await import('remark-gfm');
        remarkGfmRef.current = remarkGfmModule.default;
        setIsRemarkGfmLoaded(true);
      }
    };

    loadRemarkGfm();
  }, []);

  const mergedComponents = useMemo(
    (): Components => ({
      ...markdownTextComponents,
      a: defaultLinkComponent,
      code: defaultCodeComponent as NonNullable<Components['code']>,
      ...components,
    }),
    [components]
  );

  return (
    <Box sx={sx}>
      <Suspense
        fallback={
          <Box sx={{ pt: 6 }}>
            <Skeleton variant="text" width="75%" />
            <Skeleton variant="text" width="55%" />
          </Box>
        }
      >
        <Markdown
          components={mergedComponents}
          remarkPlugins={
            isRemarkGfmLoaded && remarkGfmRef.current
              ? [remarkGfmRef.current as never]
              : []
          }
        >
          {text}
        </Markdown>
      </Suspense>
    </Box>
  );
};
